import { useEffect, useRef, useCallback } from 'react';
import { useConversations } from '../hooks';
import { ConversationCard, ConversationSearch } from '../ui';

/**
 * ConversationsPage - Página de listagem de conversas
 *
 * Features:
 * - Lista de conversas agrupadas por data
 * - Busca por título
 * - Infinite scroll
 * - Delete com confirmação
 *
 * Rota: /conversas
 */
export const ConversationsPage = () => {
  const {
    groupedConversations,
    loading,
    loadingMore,
    error,
    hasMore,
    searchQuery,
    setSearchQuery,
    loadMore,
    deleteConversation,
  } = useConversations();

  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Setup infinite scroll observer
  const setupObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }
  }, [hasMore, loadingMore, loadMore]);

  useEffect(() => {
    setupObserver();
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [setupObserver]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-[calc(100vh-56px)] md:min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-[#333333] border-t-[#F4CC04] rounded-full animate-spin" />
          <span className="text-[#666666] text-sm">Carregando conversas...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-[calc(100vh-56px)] md:min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#F4CC04] text-[#1a1a1a] rounded-lg font-medium hover:bg-[#f5d633] transition-colors"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-4 md:px-8 pt-6 md:pt-8 pb-4 md:pb-6 border-b border-[#333333]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-2">
          <div>
            <p className="text-[#666666] text-sm mb-1">Conversas</p>
            <h1 className="text-white text-xl md:text-2xl font-bold">Conversas</h1>
          </div>

          {/* Search */}
          <div className="flex-1 sm:flex-none sm:w-64">
            <ConversationSearch
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>
        </div>
      </div>

      {/* Content - Container com altura limitada e scroll interno */}
      <div className="flex-1 min-h-0 px-4 md:px-8 py-4 md:py-6">
        {groupedConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <p className="text-[#666666] text-lg mb-2">Nenhuma conversa encontrada</p>
            <p className="text-[#444444] text-sm">
              {searchQuery
                ? 'Tente buscar por outro termo'
                : 'Comece uma nova conversa clicando em "Novo Chat"'
              }
            </p>
          </div>
        ) : (
          <div className="h-full max-h-[calc(100vh-200px)] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-[#333333] scrollbar-track-transparent">
            <div className="space-y-8">
              {groupedConversations.map((group) => (
                <div key={group.label}>
                  {/* Group label */}
                  <h2 className="text-[#666666] text-xs font-semibold uppercase tracking-wider mb-4 sticky top-0 bg-[#242424] py-2 -mt-2 z-10">
                    {group.label}
                  </h2>

                  {/* Conversations */}
                  <div className="space-y-3">
                    {group.conversations.map((conversation) => (
                      <ConversationCard
                        key={conversation.id}
                        conversation={conversation}
                        onDelete={deleteConversation}
                      />
                    ))}
                  </div>
                </div>
              ))}

              {/* Load more trigger */}
              {hasMore && (
                <div ref={loadMoreRef} className="py-4 flex justify-center">
                  {loadingMore && (
                    <div className="flex items-center gap-2 text-[#666666] text-sm">
                      <div className="w-4 h-4 border-2 border-[#333333] border-t-[#F4CC04] rounded-full animate-spin" />
                      <span>Carregando mais...</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
